import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import ExportConfirmationModal from "#/components/overlays/ExportConfirmationModal";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useClient } from "#/hook/useClient";
import { usePermission } from "#/hooks/usePermission";
import { exportToCSV } from "#/utils/CsvUtils";
import { isColumnVisible } from "#/utils/columnVisibilityUtils";
import { formatDate, formatDateTimeForExport, formatTime } from "#/utils/dateTimeUtils";
import { Button, Table, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconDownload, IconEye, IconPlus } from "@tabler/icons-react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CreateSiteModal from "./components/CreateSiteModal";

const SitesPage = () => {
  const navigate = useNavigate();
  const { canWrite } = usePermission();
  const { t } = useTranslation();
  const { client } = useClient();
  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [
    exportModalOpened,
    { open: openExportModal, close: closeExportModal },
  ] = useDisclosure(false);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const headers = [
    { key: "name", value: t("sites.columns.name") },
    { key: "location_text", value: t("sites.columns.location") },
    { key: "updated_at", value: t("sites.columns.lastUpdated") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const prepareExportData = (sitesToExport: typeof items) => {
    return sitesToExport.map((item) => ({
      id: item.id || "",
      name: item.name || "",
      location_text: item.location_text || "",
      latitude: item.latitude || "",
      longitude: item.longitude || "",
      updated_at: formatDateTimeForExport(item.updatedAt),
      updated_by: item.updated_by?.name || item.updated_by?.email || "",
    }));
  };

  const handleExportCurrentPage = () => {
    if (!items || items.length === 0) return;

    const exportData = prepareExportData(items);
    const exportHeaders = [
      "id",
      "name",
      "location_text",
      "latitude",
      "longitude",
      "updated_at",
      "updated_by",
    ];

    const headerLabels = {
      id: "ID",
      name: "Name",
      location_text: "Location",
      latitude: "Latitude",
      longitude: "Longitude",
      updated_at: "Last Updated",
      updated_by: "Updated By",
    };

    const note = t("common.utcNote");
    const filename = `sites-export-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
    exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
  };

  const handleExportAll = async () => {
    try {
      const response = await sitesApi.getAll({
        limit: total,
        page: 1,
        ...(sortBy ? { sortBy: sortBy } : {}),
        ...(sortDirection ? { order: sortDirection } : {}),
      });

      const allSites = response.data.data;
      const exportData = prepareExportData(allSites);

      const exportHeaders = [
        "id",
        "name",
        "location_text",
        "latitude",
        "longitude",
        "updated_at",
        "updated_by",
      ];

      const headerLabels = {
        id: "ID",
        name: "Name",
        location_text: "Location",
        latitude: "Latitude",
        longitude: "Longitude",
        updated_at: "Last Updated",
        updated_by: "Updated By",
      };

      const note = t("common.utcNote");
      const filename = `sites-export-all-${moment().format("YYYY-MM-DD-HHmmss")}.csv`;
      exportToCSV(exportData, exportHeaders, filename, headerLabels, note);
    } catch (error) {
      console.error("Failed to export all sites:", error);
    }
  };

  const {
    data: { items = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getSites,
  } = useQuery({
    queryKey: ["items", sortBy, page, sortBy, sortDirection],
    queryFn: async () => {
      try {
        const response = await sitesApi.getAll({
          limit: limit,
          page: page,
          ...(sortBy ? { sortBy: sortBy } : {}),
          ...(sortDirection ? { order: sortDirection } : {}),
        });
        return {
          items: response.data.data,
          total: response.data.total,
          per_page: response.data.limit,
        };
      } catch (error) {
        console.log("🚀 ~ queryFn: ~ error:", error);
        throw new Error("No items returned");
      }
    },
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    retry: false,
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex items-center justify-between bg-white z-10">
        <Title order={2}>{t("sites.title")}</Title>
        <div className="w-fit flex items-center justify-center gap-4">
          {/* <Popover
						anchor={
							<Button
								leftSection={<IconFilter size={18} />}
								variant={"outline"}
								color="black"
								size="sm"
								loading={isLoading}
							>
								Filters
							</Button>
						}
					>
						<div>sup</div>
					</Popover> */}
          <PermissionTooltip disabled={!canWrite()}>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => {
                openCreateModal();
              }}
              color="black"
              size="sm"
              disabled={!canWrite()}
            >
              {t("sites.createNewSite")}
            </Button>
          </PermissionTooltip>
          <Button
            leftSection={<IconDownload size={18} />}
            variant="outline"
            onClick={openExportModal}
            disabled={!items || items.length === 0}
            size="sm"
          >
            {t("common.exportCsv")}
          </Button>
          <Pagination
            page={page}
            total={total}
            limit={per_page}
            onChange={setPage}
            onLimitChange={(newLimit) => {
              setLimit(newLimit);
              setPage(1);
            }}
          />
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.filter((item) => item.key === "actions" || isColumnVisible("sites", item.key, client)).map((item) => item.value)}
          loading={isPending}
          allowSorting={false}
          sortableColumns={[1, 2, 3]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 5,
          }}
          rows={items.map((item) => {
            return (
              <Table.Tr key={item.id}>
                {isColumnVisible("sites", "name", client) && (
                  <Table.Td className="capitalize" style={{ minWidth: '180px' }}>{item.name ?? "--"}</Table.Td>
                )}
                {isColumnVisible("sites", "location_text", client) && (
                  <Table.Td style={{ minWidth: '250px' }}>{item.location_text ?? "--"}</Table.Td>
                )}
                {isColumnVisible("sites", "updated_at", client) && (
                  <Table.Td style={{ minWidth: '200px' }}>
                    <div className="w-full flex flex-col ">
                      <span>
                        {formatDate(item.updatedAt, client)}
                        <br />
                        {formatTime(item.updatedAt, client)}
                      </span>
                      {item.updated_by && (
                        <div>
                          {"by"}
                          <b> {item.updated_by.name ?? item.updated_by.email}</b>
                        </div>
                      )}
                    </div>
                  </Table.Td>
                )}
                <Table.Td style={{ minWidth: '100px' }}>
                  <div className="w-full flex items-center justify-center">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/sites/${item.id}`);
                      }}
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <CreateSiteModal
        opened={createModalOpened}
        onClose={() => {
          closeCreateModal();
        }}
        onSuccess={() => {
          setPage(1);
          void getSites();
          closeCreateModal();
        }}
      />

      <ExportConfirmationModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        onExportCurrentPage={handleExportCurrentPage}
        onExportAll={handleExportAll}
        currentPageCount={items.length}
        totalCount={total}
      />
    </div>
  );
};

export default SitesPage;
